import React from 'react';
import {
  BottomNavigation as MuiBottomNavigation,
  BottomNavigationAction,
  Paper,
  Badge,
} from '@mui/material';
import { MenuItem } from '../../types';
import { useCommunicationCounts } from '../../hooks/useCommunicationCounts';

interface BottomNavigationProps {
  menuItems: MenuItem[];
  selectedMenu: string;
  onMenuSelect: (menuId: string) => void;
  maxItems?: number;
}

export const BottomNavigation: React.FC<BottomNavigationProps> = ({
  menuItems,
  selectedMenu,
  onMenuSelect,
  maxItems = 5,
}) => {
  // Filter to most important menu items for mobile (skip hidden registry-only entries). `desktopOnly`
  // items (e.g. Smart Sampling — its map editors need a pointer) are excluded EXPLICITLY, not by the
  // slice, so the bottom nav keeps exactly its 5 items even as the desktop sidebar gains a 6th.
  const mobileMenuItems = menuItems.filter((i) => !i.hidden && !i.desktopOnly).slice(0, maxItems);

  // Live Communication badge (leaf consumption — see Sidebar). Merged over the static item.badge.
  const { counts } = useCommunicationCounts();
  const communicationTotal = (counts?.requestedBookings ?? 0) + (counts?.newMessages ?? 0);
  // newIntake/newFiles/newSignups are internal-only (partner counts endpoint omits them) — internal
  // badge sum ONLY; the partner badge stays bookings + messages.
  const internalCommunicationTotal =
    communicationTotal +
    (counts?.newIntake ?? 0) +
    (counts?.newFiles ?? 0) +
    (counts?.newSignups ?? 0);
  const badgeFor = (item: MenuItem): number | undefined => {
    if (item.id === 'communication') {
      return internalCommunicationTotal > 0 ? internalCommunicationTotal : undefined;
    }
    if (item.id === 'partner-communication') {
      return communicationTotal > 0 ? communicationTotal : undefined;
    }
    return item.badge;
  };

  return (
    <Paper 
      sx={{ 
        position: 'fixed', 
        bottom: 0, 
        left: 0, 
        right: 0, 
        zIndex: 1000,
        borderTop: '1px solid',
        borderColor: 'divider',
      }} 
      elevation={3}
    >
      <MuiBottomNavigation
        value={selectedMenu}
        onChange={(_, newValue) => onMenuSelect(newValue)}
        sx={{
          height: 64,
          '& .MuiBottomNavigationAction-root': {
            minWidth: 0,
            fontSize: '0.75rem',
            '& .MuiBottomNavigationAction-label': {
              fontSize: '0.75rem',
              marginTop: '4px',
            },
          },
          '& .Mui-selected': {
            color: 'primary.main',
            '& .MuiBottomNavigationAction-label': {
              fontSize: '0.75rem',
            },
          },
        }}
      >
        {mobileMenuItems.map((item) => (
          <BottomNavigationAction
            key={item.id}
            value={item.id}
            label={item.label}
            icon={
              badgeFor(item) ? (
                <Badge badgeContent={badgeFor(item)} color="error" max={99}>
                  {item.icon}
                </Badge>
              ) : (
                item.icon
              )
            }
            sx={{
              '&.Mui-selected': {
                color: 'primary.main',
              },
            }}
          />
        ))}
      </MuiBottomNavigation>
    </Paper>
  );
};